import { cva, type VariantProps } from "class-variance-authority"
import { Check, X } from "lucide-react"
import * as React from "react"

import { cn } from "../lib/utils"

// Single Selector - for selecting one option from a list
const singleSelectorVariants = cva(
  "flex w-full items-center gap-2 rounded-lg bg-background px-3 py-3 min-h-12 cursor-pointer transition-colors",
  {
    variants: {
      status: {
        unselected: "border-transparent",
        selected: "border border-primary",
      },
    },
    defaultVariants: {
      status: "unselected",
    },
  }
)

export interface SingleSelectorProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">,
    VariantProps<typeof singleSelectorVariants> {
  value?: string
  placeholder?: string
  onClear?: () => void
  onChange?: (value: string) => void
}

const SingleSelector = React.forwardRef<HTMLDivElement, SingleSelectorProps>(
  (
    {
      className,
      status,
      value,
      placeholder = "オプション",
      onClear,
      onChange: _onChange,
      onClick,
      ...props
    },
    ref
  ) => {
    const hasValue = !!value
    const currentStatus = hasValue ? "selected" : status

    return (
      <div
        ref={ref}
        className={cn(singleSelectorVariants({ status: currentStatus, className }))}
        onClick={onClick}
        {...props}
      >
        {hasValue && onClear && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onClear()
            }}
            className="shrink-0 text-text hover:text-gray"
          >
            <X className="size-5" />
          </button>
        )}
        <span
          className={cn(
            "flex-1 text-body-lg",
            hasValue ? "text-text font-normal" : "text-placeholder font-normal"
          )}
        >
          {value || placeholder}
        </span>
      </div>
    )
  }
)
SingleSelector.displayName = "SingleSelector"

// Multi Selector - for selecting multiple options with checkboxes
const multiSelectorVariants = cva(
  "flex w-full items-center gap-2 px-3 py-2 min-h-9 cursor-pointer transition-colors",
  {
    variants: {
      status: {
        unselected: "",
        selected: "",
        invalid: "opacity-50 cursor-not-allowed",
      },
    },
    defaultVariants: {
      status: "unselected",
    },
  }
)

export interface MultiSelectorOption {
  value: string
  label: string
  disabled?: boolean
}

export interface MultiSelectorProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  options: MultiSelectorOption[]
  value?: string[]
  onChange?: (value: string[]) => void
}

const MultiSelector = React.forwardRef<HTMLDivElement, MultiSelectorProps>(
  ({ className, options, value = [], onChange, ...props }, ref) => {
    const handleToggle = (optionValue: string, disabled?: boolean) => {
      if (disabled) return
      const newValue = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue]
      onChange?.(newValue)
    }

    return (
      <div ref={ref} className={cn("flex flex-col", className)} {...props}>
        {options.map((option) => {
          const isSelected = value.includes(option.value)
          const status = option.disabled ? "invalid" : isSelected ? "selected" : "unselected"

          return (
            <div
              key={option.value}
              className={cn(multiSelectorVariants({ status }))}
              onClick={() => handleToggle(option.value, option.disabled)}
            >
              <div
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-sm border transition-colors",
                  isSelected
                    ? "border-primary bg-primary text-bg-white"
                    : option.disabled
                      ? "border-disabled bg-background"
                      : "border-border bg-bg-white"
                )}
              >
                {isSelected && <Check className="size-3" />}
              </div>
              <span
                className={cn(
                  "flex-1 text-body-md",
                  option.disabled ? "text-text-disabled" : "text-text"
                )}
              >
                {option.label}
              </span>
            </div>
          )
        })}
      </div>
    )
  }
)
MultiSelector.displayName = "MultiSelector"

export { SingleSelector, MultiSelector, singleSelectorVariants, multiSelectorVariants }
